import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import BlogBreadcrumb from "@/components/blog/BlogBreadcrumb";
import ReadingProgressBar from "@/components/blog/ReadingProgressBar";
import GhostContent from "@/components/blog/GhostContentStyles";
import AuthorCard from "@/components/blog/AuthorCard";
import BlogPostCard from "@/components/blog/BlogPostCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, Calendar, ArrowLeft, Languages } from "lucide-react";
import { optimizeGhostImage, formatBlogDate, generateArticleJsonLd, generateBreadcrumbJsonLd } from "@/lib/blog-utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface Post {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  html: string;
  featureImage?: string | null;
  featureImageAlt?: string;
  publishedAt: string;
  updatedAt?: string;
  readingTime?: number;
  primaryTag?: {
    name: string;
    slug: string;
    description?: string;
  } | null;
  tags: Array<{ name: string; slug: string }>;
  primaryAuthor: {
    id: string;
    name: string;
    slug: string;
    profileImage?: string | null;
    bio?: string | null;
    location?: string | null;
    website?: string | null;
    twitter?: string | null;
  };
  authors: Array<{
    name: string;
    slug: string;
    profileImage?: string | null;
  }>;
  meta: {
    title: string;
    description: string;
    ogImage?: string | null;
    ogTitle?: string;
    ogDescription?: string;
    canonicalUrl?: string;
  };
  relatedPosts: Array<{
    id: string;
    slug: string;
    title: string;
    excerpt: string;
    featureImage?: string | null;
    featureImageAlt?: string;
    publishedAt: string;
    readingTime?: number;
    primaryTag?: { name: string; slug: string } | null;
    primaryAuthor: { name: string; slug: string; profileImage?: string | null };
  }>;
  isTranslated?: boolean;
  needsTranslation?: boolean;
  originalPostId?: string;
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const { language } = useLanguage();
  const { getLocalizedPath } = useLocalizedPath();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);

  // Determine if we're on English path
  const isEnglish = location.pathname.startsWith("/en");
  const currentLang = isEnglish ? "en" : "th";

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);

      try {
        const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-blog-post`);
        url.searchParams.set("slug", slug);
        url.searchParams.set("lang", currentLang);

        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
        });

        const data = await response.json();

        if (!data.success) {
          console.error("Blog post fetch error:", data.error);
          setError("not_found");
          return;
        }

        // If English but needs translation, trigger translation
        if (currentLang === "en" && data.post.needsTranslation) {
          setPost(data.post);
          triggerTranslation(data.post);
        } else {
          setPost(data.post);
        }
      } catch (err) {
        console.error("Error fetching post:", err);
        setError("load_error");
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
    window.scrollTo(0, 0);
  }, [slug, currentLang]);

  const triggerTranslation = async (postData: Post) => {
    if (!postData.originalPostId) return;

    setTranslating(true);
    try {
      const { data, error } = await supabase.functions.invoke("translate-blog-post", {
        body: {
          slug: postData.slug,
          ghostPostId: postData.originalPostId,
          title: postData.title,
          excerpt: postData.excerpt,
          html: postData.html,
          metaTitle: postData.meta.title,
          metaDescription: postData.meta.description,
        },
      });

      if (data?.success && data.translation) {
        setPost({
          ...postData,
          title: data.translation.title,
          excerpt: data.translation.excerpt,
          html: data.translation.html,
          meta: {
            ...postData.meta,
            title: data.translation.metaTitle,
            description: data.translation.metaDescription,
            ogTitle: data.translation.title,
            ogDescription: data.translation.excerpt,
          },
          isTranslated: true,
          needsTranslation: false,
        });
      }
    } catch (err) {
      console.error("Translation error:", err);
    } finally {
      setTranslating(false);
    }
  };

  const SITE_URL = "https://carbonwize.io";
  const canonicalPath = `/blog/${post?.slug || slug}`;
  const canonicalUrl = currentLang === "en" ? `${SITE_URL}/en${canonicalPath}` : `${SITE_URL}${canonicalPath}`;
  const thUrl = `${SITE_URL}${canonicalPath}`;
  const enUrl = `${SITE_URL}/en${canonicalPath}`;

  const breadcrumbItems = [
    { label: "Blog", href: getLocalizedPath("/blog") },
    ...(post?.primaryTag
      ? [{ label: post.primaryTag.name, href: getLocalizedPath(`/blog/tag/${post.primaryTag.slug}`) }]
      : []),
    ...(post ? [{ label: post.title }] : []),
  ];

  const articleJsonLd = post ? generateArticleJsonLd(post) : null;
  const breadcrumbJsonLd = post
    ? generateBreadcrumbJsonLd([
        { name: "Home", url: getLocalizedPath("/") },
        { name: "Blog", url: getLocalizedPath("/blog") },
        ...(post.primaryTag
          ? [{ name: post.primaryTag.name, url: getLocalizedPath(`/blog/tag/${post.primaryTag.slug}`) }]
          : []),
        { name: post.title, url: getLocalizedPath(`/blog/${post.slug}`) },
      ])
    : null;

  if (error) {
    return (
      <>
        <SEOHead
          title="Post Not Found | Carbonwize Blog"
          description="Post not found"
          path="/blog"
          noIndex
        />
        <div className="min-h-screen bg-background">
          <Navbar />
          <main className="pt-24 pb-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                {currentLang === "th" ? "ไม่พบบทความ" : "Post Not Found"}
              </h1>
              <p className="text-muted-foreground mb-8">
                {currentLang === "th"
                  ? "บทความที่คุณกำลังมองหาไม่พบหรือไม่สามารถใช้งานได้ชั่วคราว"
                  : "The article you're looking for could not be found or is temporarily unavailable."}
              </p>
              <Link
                to={getLocalizedPath("/blog")}
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      {post && (
        <SEOHead
          title={`${post.meta.title} | Carbonwize Blog`}
          description={post.meta.description}
          path={`/blog/${post.slug}`}
          ogImage={post.meta.ogImage || undefined}
          ogType="article"
          publishedTime={post.publishedAt}
          modifiedTime={post.updatedAt}
          author={post.primaryAuthor.name}
          structuredData={[
            articleJsonLd,
            breadcrumbJsonLd
          ].filter(Boolean) as object[]}
        />
      )}

      <ReadingProgressBar />

      <div className="min-h-screen bg-background">
        <Navbar />

        <main className="pt-24 pb-16">
          <article className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            {loading ? (
              <div className="space-y-6">
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-96 w-full rounded-2xl" />
                <div className="space-y-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ) : post ? (
              <>
                <BlogBreadcrumb items={breadcrumbItems} />

                {/* Translation indicator */}
                {currentLang === "en" && (post.isTranslated || translating) && (
                  <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-sm text-blue-700">
                    <Languages className="w-4 h-4" />
                    {translating ? "Translating content..." : "This content has been auto-translated from Thai."}
                    <Link to={`/blog/${post.slug}`} className="ml-auto text-blue-600 hover:underline">
                      View original (Thai)
                    </Link>
                  </div>
                )}

                {/* Header */}
                <header className="mb-8">
                  {post.primaryTag && (
                    <Link
                      to={getLocalizedPath(`/blog/tag/${post.primaryTag.slug}`)}
                      className="inline-block px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full mb-4 hover:bg-primary/90 transition-colors"
                    >
                      {post.primaryTag.name}
                    </Link>
                  )}

                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground mb-6 leading-tight">
                    {post.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
                    <Link
                      to={getLocalizedPath(`/blog/author/${post.primaryAuthor.slug}`)}
                      className="flex items-center gap-2 hover:text-foreground transition-colors"
                    >
                      {post.primaryAuthor.profileImage ? (
                        <img
                          src={optimizeGhostImage(post.primaryAuthor.profileImage, 48)}
                          alt={post.primaryAuthor.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">{post.primaryAuthor.name.charAt(0)}</span>
                        </div>
                      )}
                      <span className="font-medium">{post.primaryAuthor.name}</span>
                    </Link>

                    <span>•</span>

                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <time dateTime={post.publishedAt}>{formatBlogDate(post.publishedAt)}</time>
                    </div>

                    <span>•</span>

                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{post.readingTime || 5} min read</span>
                    </div>
                  </div>
                </header>

                {/* Feature Image */}
                {post.featureImage && (
                  <div className="mb-10 -mx-4 sm:mx-0">
                    <img
                      src={optimizeGhostImage(post.featureImage, 1200)}
                      alt={post.featureImageAlt || post.title}
                      className="w-full h-auto rounded-none sm:rounded-2xl object-cover max-h-[500px]"
                    />
                  </div>
                )}

                {/* Content */}
                <div className="prose-container">
                  <GhostContent html={post.html} />
                </div>

                {/* Tags */}
                {post.tags.length > 0 && (
                  <div className="mt-12 pt-8 border-t border-border">
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <Link
                          key={tag.slug}
                          to={getLocalizedPath(`/blog/tag/${tag.slug}`)}
                          className="px-3 py-1 bg-secondary text-secondary-foreground text-sm rounded-full hover:bg-secondary/80 transition-colors"
                        >
                          {tag.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Author Bio */}
                <div className="mt-12 p-6 bg-secondary/30 rounded-2xl">
                  <AuthorCard author={post.primaryAuthor} variant="full" />
                </div>

                {/* Related Posts */}
                {post.relatedPosts.length > 0 && (
                  <section className="mt-16">
                    <h2 className="text-2xl font-bold text-foreground mb-8">Related Articles</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                      {post.relatedPosts.map((relatedPost) => (
                        <BlogPostCard key={relatedPost.id} post={relatedPost} showAuthor={false} />
                      ))}
                    </div>
                  </section>
                )}
              </>
            ) : null}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogPost;
